import { PartyContactResponse } from 'compliance-api/generated';
import { useMemo, useState } from 'react';
import PartyContactModal from '@/modules/admin-dashboard/components/contractManagement/PartyContactModal.tsx';
import { useDeletePartyContactMutation } from '@/modules/admin-dashboard/queries/contract-management.ts';
import { Button, ConfirmModal, SearchField } from 'ui';
import { EditGrayIcon, PlusIcon, TrashGrayIcon } from 'ui/icons';

export const PartyContactsTab = ({
  partyId,
  contacts,
  readOnly = false,
}: {
  partyId: number;
  contacts: PartyContactResponse[];
  readOnly?: boolean;
}) => {
  const [search, setSearch] = useState('');
  const [openContactModal, setOpenContactModal] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<PartyContactResponse>();
  const [contactToDelete, setContactToDelete] = useState<PartyContactResponse>();
  const { mutateAsync: deletePartyContact } = useDeletePartyContactMutation();

  const filteredContacts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return contacts;

    return contacts.filter(contact =>
      [contact.full_name, contact.email, contact.phone].some(value =>
        value?.toLowerCase().includes(normalizedSearch),
      ),
    );
  }, [contacts, search]);

  const handleCreateContact = () => {
    if (readOnly) return;
    setContactToEdit(undefined);
    setOpenContactModal(true);
  };

  const handleEditContact = (contact: PartyContactResponse) => {
    if (readOnly) return;
    setContactToEdit(contact);
    setOpenContactModal(true);
  };

  const handleDeleteContact = (contact: PartyContactResponse) => {
    if (readOnly) return;
    setContactToDelete(contact);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <SearchField
            placeholder="Search"
            value={search}
            onChange={setSearch}
            allowClear
            className="min-w-0 flex-1"
            disabled={!filteredContacts?.length}
          />
          <Button
            className="h-10 min-h-[40px] w-10 min-w-[40px] px-0 py-0"
            onClick={handleCreateContact}
            disabled={readOnly}
          >
            <img src={PlusIcon} alt="add contact" className="h-5 w-5 invert" />
          </Button>
        </div>

        <div className="space-y-3">
          {filteredContacts.map(contact => (
            <PartyContactCard
              key={contact.id}
              contact={contact}
              onEdit={handleEditContact}
              onDelete={handleDeleteContact}
              readOnly={readOnly}
            />
          ))}

          {filteredContacts.length === 0 && (
            <div className="rounded border border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
              No contacts found.
            </div>
          )}
        </div>
      </div>

      <PartyContactModal
        partyId={partyId}
        contact={contactToEdit}
        open={openContactModal}
        onClose={() => setOpenContactModal(false)}
      />

      {contactToDelete && (
        <ConfirmModal
          title="Delete contact"
          content={`Are you sure you want to delete ${
            contactToDelete.full_name || 'this contact'
          }?`}
          open={!!contactToDelete}
          onClose={() => setContactToDelete(undefined)}
          onConfirm={async () => {
            await deletePartyContact({ id: contactToDelete.id, partyId });
          }}
        />
      )}
    </>
  );
};

const PartyContactCard = ({
  contact,
  onEdit,
  onDelete,
  readOnly,
}: {
  contact: PartyContactResponse;
  onEdit: (contact: PartyContactResponse) => void;
  onDelete: (contact: PartyContactResponse) => void;
  readOnly: boolean;
}) => (
  <div className="flex gap-3 rounded border border-gray-200 px-3 py-3">
    <div className="min-w-0 flex-1 text-sm leading-[22px]">
      <div className="font-medium text-gray-900">{contact.full_name || '-'}</div>
      <div className="text-gray-500">{contact.email || '-'}</div>
      <div className="text-gray-500">{contact.phone || '-'}</div>
      <div className="text-gray-500">
        Authorised signatory: {contact.is_authorised_signatory ? 'Yes' : 'No'}
      </div>
    </div>

    <div className="flex shrink-0 items-start gap-4">
      <button
        type="button"
        className="hover:opacity-80"
        onClick={() => onEdit(contact)}
        aria-label="Edit contact"
        disabled={readOnly}
      >
        <img src={EditGrayIcon} alt="" className="h-5 w-5" />
      </button>
      <button
        type="button"
        className="hover:opacity-80"
        onClick={() => onDelete(contact)}
        aria-label="Delete contact"
        disabled={readOnly}
      >
        <img src={TrashGrayIcon} alt="" className="h-5 w-5" />
      </button>
    </div>
  </div>
);
